<script setup lang="ts">
import { computed, ref } from 'vue';
import { Download, MonitorDown, Smartphone, X } from 'lucide-vue-next';
import {
	dismissInstallPromptForSession,
	hasInstallPromptSessionBypass,
	isMobileDevice,
	usePwaInstall,
} from '@/presentation/composables/usePwaInstall.composable';
import useSharedStore from '@/presentation/store/shared';

const route = useRoute();
const sharedStore = useSharedStore();
const isHidden = ref(hasInstallPromptSessionBypass());
const isMobile = isMobileDevice();
const { isInstalled, isReady } = usePwaInstall();

const installPath = computed(() => ({
	name: 'InstallApp',
	query: { redirect: route.fullPath, source: 'suggestion' },
}));

const shouldShow = computed(() => isReady.value && !isInstalled.value && !isHidden.value);
const isLoggedIn = computed(() => sharedStore.userInformation?.loggedIn === true);
const isBusiness = computed(() => sharedStore.userInformation?.role === 'Business');
const benefitText = computed(() => {
	if (!isLoggedIn.value) {
		return isMobile
			? 'سریع‌تر وارد نیوتی شوید و همه امکانات همیشه دم دست‌تان باشد.'
			: 'نیوتی را مستقیم از کامپیوتر باز کنید؛ بدون اینکه هر بار دنبال آدرس سایت بگردید.';
	}

	if (isBusiness.value) {
		return isMobile
			? 'نوبت‌های جدید را سریع‌تر ببینید و مدیریت سالن همیشه دم دست‌تان باشد.'
			: 'بدون جست‌وجوی دوباره، مستقیم از کامپیوتر وارد مدیریت سالن شوید.';
	}

	return isMobile ? 'سریع‌تر نوبت بگیرید و نوبت‌های خودتان را راحت‌تر پیگیری کنید.' : 'بدون جست‌وجوی دوباره، سریع وارد نیوتی شوید و نوبت بگیرید.';
});

const dismiss = () => {
	dismissInstallPromptForSession();
	isHidden.value = true;
};
</script>

<template>
	<aside
		v-if="shouldShow"
		dir="rtl"
		aria-label="پیشنهاد نصب نیوتی"
		class="fixed inset-x-3 bottom-[calc(var(--app-bottom-nav-height,5.5rem)+1.25rem)] z-40 mx-auto max-w-lg rounded-2xl border border-indigo-100 bg-white p-4 shadow-[0_18px_48px_rgba(31,41,82,0.18)] md:bottom-auto md:left-auto md:right-5 md:top-5 md:mr-0 md:w-[390px]"
	>
		<button
			type="button"
			class="absolute left-3 top-3 grid h-9 w-9 place-items-center rounded-full bg-slate-100 text-slate-500"
			aria-label="فعلاً نمایش نده"
			@click="dismiss"
		>
			<X :size="18" />
		</button>
		<div class="flex items-start gap-3 pl-9">
			<span class="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-indigo-50 text-primary">
				<Smartphone v-if="isMobile" :size="25" />
				<MonitorDown v-else :size="25" />
			</span>
			<div>
				<p class="text-sm font-black text-slate-950">نیوتی را نصب کنید</p>
				<p class="mt-1 text-xs font-bold leading-6 text-slate-500">{{ benefitText }}</p>
			</div>
		</div>
		<div class="mt-3 grid grid-cols-[1fr_auto] gap-2">
			<RouterLink :to="installPath" class="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 text-xs font-black text-white">
				<Download :size="17" />
				نصب نیوتی
			</RouterLink>
			<button type="button" class="min-h-11 rounded-xl bg-slate-100 px-4 text-xs font-black text-slate-600" @click="dismiss">الان نه</button>
		</div>
	</aside>
</template>
